import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import { useDeleteSocialOrderMutation, useGetSocialOrderQuery } from '../../../api/socialOrders'
import { NotFound } from '../../../components/ui-kit/NotFound'
import { routesProfile } from '../../../consts'
import { EditSocialOrderTabs } from './Tabs'

export const EditSocialOrder = () => {
  useLayoutEffect(() => {
    document.title = 'Редактирование социального заказа'
  }, [])

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [notFound, setNotFound] = useState(false)
  const [noAccess, setNoAccess] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const orderId = id ? parseInt(id, 10) : NaN
  const { data, isLoading, isError } = useGetSocialOrderQuery({ id: orderId }, { skip: Number.isNaN(orderId) })
  const [deleteSocialOrder, { isLoading: isDeletingSocialOrder }] = useDeleteSocialOrderMutation()

  const mySocialOrdersPath = useMemo(() => `/${routesProfile.SOCIALORDERS.path}/mine`, [])

  useEffect(() => {
    if (Number.isNaN(orderId)) {
      setNotFound(true)
      return
    }
    if (!isLoading && (isError || !data)) {
      setNotFound(true)
      return
    }
    if (data && data.viewer?.permissions?.can_edit_content !== true) {
      setNoAccess(true)
    }
  }, [orderId, data, isLoading, isError])

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (notFound) {
    return <NotFound text="Социальный заказ не найден" />
  }

  if (noAccess) {
    return <NotFound text="Нет доступа" />
  }

  if (!data) {
    return null
  }

  const canDeleteSocialOrder = data.viewer?.permissions?.can_delete_social_order === true

  const handleConfirmDelete = async () => {
    try {
      await deleteSocialOrder({ socialOrderId: orderId }).unwrap()
      setIsDeleteModalOpen(false)
      toast.success('Социальный заказ удалён')
      navigate(mySocialOrdersPath, { replace: true })
    } catch (err) {
      const status = (err as { status?: number; originalStatus?: number })?.status
        ?? (err as { originalStatus?: number })?.originalStatus
      if (status === 403) {
        toast.error('Нет прав удалить социальный заказ')
        return
      }
      if (status === 404) {
        toast.error('Социальный заказ не найден или уже удалён')
        return
      }
      toast.error('Не удалось удалить социальный заказ')
    }
  }

  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.down('md')]: {
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
        },
      })}
    >
      <EditSocialOrderTabs title="Редактирование социального заказа" />

      {canDeleteSocialOrder && (
        <>
          <Stack alignItems="center" px={1.5} width="100%" sx={{ mt: 2 }}>
            <Stack width="100%" maxWidth={{ xs: '736px', md: '840px' }}>
              <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
                <Stack gap={1.5}>
                  <Typography variant="black-16-semiBold">Опасная зона</Typography>
                  <Typography variant="black-14-normal" color="text.secondary">
                    Удаление социального заказа необратимо. Перед удалением убедитесь, что вам больше
                    не нужны данные заказа.
                  </Typography>
                  <Stack direction="row">
                    <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)}>
                      Удалить социальный заказ
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          </Stack>

          <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
            <DialogTitle>Удалить социальный заказ</DialogTitle>
            <DialogContent>
              <Typography variant="black-14-normal">
                Социальный заказ будет удалён и исчезнет из личного кабинета. Действие нельзя отменить.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                variant="follow"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeletingSocialOrder}
              >
                Отмена
              </Button>
              <Button
                variant="danger"
                onClick={() => void handleConfirmDelete()}
                disabled={isDeletingSocialOrder}
              >
                {isDeletingSocialOrder ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} color="inherit" />
                    <span>Удаление...</span>
                  </Stack>
                ) : (
                  'Удалить'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  )
}
